import {
  AlertCircle,
  BadgePercent,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  IndianRupee,
  Loader2,
  MapPin,
  Phone,
  Save,
  ShieldCheck,
  Sliders,
  ToggleLeft,
  ToggleRight,
  Printer,
  Tag,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TaxSlab {
  threshold_amount: number;
  low_rate_percentage: number;
  high_rate_percentage: number;
}

interface PrintConfig {
  paper_size: '58mm' | '80mm';
  show_address: boolean;
  show_mobile: boolean;
  show_gstin: boolean;
  show_customer: boolean;
  show_loyalty: boolean;
  footer_message: string;
  label_size: '2x1' | '3x2' | '4x3';
  show_brand: boolean;
  show_category: boolean;
  show_mrp: boolean;
  connection_mode: 'BLUETOOTH' | 'USB';
}

interface CompanyProfile {
  company_name: string;
  company_address: string;
  company_mobile: string;
  is_gst_eligible: boolean;
  tenant_gstin: string | null;
  tax_slabs: TaxSlab[];
  loyalty_spend_threshold?: number;
  loyalty_points_rewarded?: number;
  invoice_prefix?: string;
  next_invoice_number?: number;
  plan_features?: {
    bulkMatrix: boolean;
    gstComplianceExport: boolean;
    loyaltyEngine: boolean;
  };
  print_config?: PrintConfig;
}

const DEFAULT_SLAB: TaxSlab = {
  threshold_amount: 1000,
  low_rate_percentage: 5,
  high_rate_percentage: 12,
};

const DEFAULT_PRINT_CONFIG: PrintConfig = {
  paper_size: '58mm',
  show_address: true,
  show_mobile: true,
  show_gstin: true,
  show_customer: true,
  show_loyalty: true,
  footer_message: 'Thank you for your visit!',
  label_size: '2x1',
  show_brand: true,
  show_category: true,
  show_mrp: true,
  connection_mode: 'BLUETOOTH',
};

const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const MOBILE_REGEX = /^[6-9]\d{9}$/;

// ─── Section Header ───────────────────────────────────────────────────────────

function SectionHeader({
  icon: Icon,
  title,
  subtitle,
}: {
  icon: React.ElementType;
  title: string;
  subtitle: string;
}) {
  return (
    <div className="flex items-start gap-3 mb-5">
      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
        <Icon size={18} className="text-emerald-600" />
      </div>
      <div>
        <h3 className="text-sm font-bold text-slate-800 tracking-tight">{title}</h3>
        <p className="text-[11px] text-slate-400 mt-0.5 font-light">{subtitle}</p>
      </div>
    </div>
  );
}

// ─── Input Field ──────────────────────────────────────────────────────────────

function Field({
  label,
  id,
  error,
  children,
}: {
  label: string;
  id: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={id}
        className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide"
      >
        {label}
      </label>
      {children}
      {error && (
        <p className="text-[10px] text-rose-600 font-medium flex items-center gap-1">
          <AlertCircle size={10} />
          {error}
        </p>
      )}
    </div>
  );
}

const inputCls = (hasError?: boolean) =>
  `block w-full px-3 py-2 text-xs border rounded-lg focus:outline-none focus:ring-2 transition-all ${hasError
    ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-100 bg-rose-50'
    : 'border-slate-200 focus:border-emerald-500 focus:ring-emerald-100 bg-white text-slate-800'
  }`;

// ─── Main Component ───────────────────────────────────────────────────────────

export function CompanySettings() {
  const { user } = useAuth();
  const tenantId = user?.tenantId;

  const [tenantFlags, setTenantFlags] = useState(() => {
    try {
      const stored = localStorage.getItem(`tenant_flags_${tenantId}`);
      return stored
        ? JSON.parse(stored)
        : { bulkMatrix: false, gstComplianceExport: false, loyaltyEngine: false };
    } catch {
      return { bulkMatrix: false, gstComplianceExport: false, loyaltyEngine: false };
    }
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Form state
  const [companyName, setCompanyName] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyMobile, setCompanyMobile] = useState('');
  const [isGstEligible, setIsGstEligible] = useState(false);
  const [tenantGstin, setTenantGstin] = useState('');
  const [slab, setSlab] = useState<TaxSlab>(DEFAULT_SLAB);
  const [loyaltySpendThreshold, setLoyaltySpendThreshold] = useState<number>(100);
  const [loyaltyPointsRewarded, setLoyaltyPointsRewarded] = useState<number>(10);
  const [invoicePrefix, setInvoicePrefix] = useState('INV');
  const [nextInvoiceNumber, setNextInvoiceNumber] = useState<number>(1000);

  // Print Configuration state
  const [printConfig, setPrintConfig] = useState<PrintConfig>(DEFAULT_PRINT_CONFIG);

  // Validation errors
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});

  // GST section collapsed state
  const [slabExpanded, setSlabExpanded] = useState(true);

  // Fetch profile on mount
  useEffect(() => {
    if (!tenantId) return;
    try {
      const stored = localStorage.getItem(`tenant_flags_${tenantId}`);
      if (stored) {
        setTenantFlags(JSON.parse(stored));
      }
    } catch (e) {
      console.error(e);
    }
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get('/tenant/settings/company-profile', {
          headers: { 'x-tenant-id': tenantId },
        });
        const d: CompanyProfile = res.data.data;
        if (d.plan_features) {
          localStorage.setItem(`tenant_flags_${tenantId}`, JSON.stringify(d.plan_features));
          setTenantFlags(d.plan_features);
        }
        setCompanyName(d.company_name ?? '');
        setCompanyAddress(d.company_address ?? '');
        setCompanyMobile(d.company_mobile ?? '');
        setIsGstEligible(d.is_gst_eligible ?? false);
        setTenantGstin(d.tenant_gstin ?? '');
        setSlab(d.tax_slabs?.[0] ?? DEFAULT_SLAB);
        setLoyaltySpendThreshold(d.loyalty_spend_threshold ?? 100);
        setLoyaltyPointsRewarded(d.loyalty_points_rewarded ?? 10);
        setInvoicePrefix(d.invoice_prefix ?? 'INV');
        setNextInvoiceNumber(d.next_invoice_number ?? 1000);
        setPrintConfig(d.print_config ?? DEFAULT_PRINT_CONFIG);
      } catch {
        // Silently initialise with empty defaults on first load
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [tenantId]);

  // ─── Validation ─────────────────────────────────────────────────

  const validate = (): boolean => {
    const e: Partial<Record<string, string>> = {};

    if (!companyName.trim()) {
      e.company_name = 'Company name is required';
    }
    if (companyMobile && !MOBILE_REGEX.test(companyMobile)) {
      e.company_mobile = 'Enter a valid 10-digit Indian mobile number';
    }
    if (tenantFlags.gstComplianceExport) {
      if (isGstEligible && tenantGstin && !GSTIN_REGEX.test(tenantGstin)) {
        e.tenant_gstin = 'Enter a valid 15-character GSTIN (e.g. 29ABCDE1234F1Z5)';
      }
      if (slab.threshold_amount <= 0) {
        e.threshold_amount = 'Threshold must be a positive amount';
      }
      if (slab.low_rate_percentage < 0 || slab.low_rate_percentage > 28) {
        e.low_rate = 'GST rate must be between 0% and 28%';
      }
      if (slab.high_rate_percentage < 0 || slab.high_rate_percentage > 28) {
        e.high_rate = 'GST rate must be between 0% and 28%';
      }
    }
    if (tenantFlags.loyaltyEngine) {
      if (loyaltySpendThreshold <= 0) {
        e.loyalty_spend_threshold = 'Spend threshold must be greater than zero';
      }
      if (loyaltyPointsRewarded <= 0) {
        e.loyalty_points_rewarded = 'Points rewarded must be greater than zero';
      }
    }
    if (!invoicePrefix.trim()) {
      e.invoice_prefix = 'Invoice prefix is required';
    }
    if (nextInvoiceNumber < 1) {
      e.next_invoice_number = 'Invoice sequence must start from 1 or above';
    }
    if (printConfig.footer_message.length > 120) {
      e.footer_message = 'Footer message cannot exceed 120 characters';
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ─── Save handler ────────────────────────────────────────────────

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    setSaveStatus('idle');
    try {
      await api.put(
        '/tenant/settings/company-profile',
        {
          company_name: companyName.trim(),
          company_address: companyAddress.trim() || undefined,
          company_mobile: companyMobile.trim() || undefined,
          is_gst_eligible: tenantFlags.gstComplianceExport ? isGstEligible : false,
          tenant_gstin: tenantFlags.gstComplianceExport && isGstEligible && tenantGstin.trim() ? tenantGstin.trim() : null,
          tax_slabs: tenantFlags.gstComplianceExport && isGstEligible ? [slab] : [],
          loyalty_spend_threshold: tenantFlags.loyaltyEngine ? loyaltySpendThreshold : undefined,
          loyalty_points_rewarded: tenantFlags.loyaltyEngine ? loyaltyPointsRewarded : undefined,
          invoice_prefix: invoicePrefix.trim(),
          next_invoice_number: nextInvoiceNumber,
          print_config: printConfig,
        },
        { headers: { 'x-tenant-id': tenantId } },
      );

      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err: any) {
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  // ─── GSTIN live validation indicator ─────────────────────────────

  const gstinValid = GSTIN_REGEX.test(tenantGstin);
  const gstinTouched = tenantGstin.length > 0;

  // ─── Loading state ───────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3 text-slate-400">
          <Loader2 size={32} className="animate-spin text-emerald-500" />
          <p className="text-sm font-medium">Loading company profile…</p>
        </div>
      </div>
    );
  }

  // ─── Render ──────────────────────────────────────────────────────

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* ── Page Header ─────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-b border-slate-200 pb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Building2 size={22} className="text-emerald-600" />
            Company Configuration
          </h1>
          <p className="mt-1 text-sm text-slate-500 font-light">
            Manage your store identity, GST registration, and dynamic textile tax slab rules.
          </p>
        </div>
        {saveStatus === 'success' && (
          <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-xs font-semibold animate-fadeIn">
            <CheckCircle2 size={14} />
            Configuration saved successfully
          </div>
        )}
        {saveStatus === 'error' && (
          <div className="flex items-center gap-2 text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-xs font-semibold animate-fadeIn">
            <AlertCircle size={14} />
            Failed to save — please retry
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Column: Form Controls */}
        <div className="lg:col-span-7">
          <form onSubmit={handleSave} className="space-y-6" id="company-settings-form">
            {/* ── Section A: Profile Identity ──────────────────────── */}
            <div className="card">
              <SectionHeader
                icon={Building2}
                title="Profile Identity"
                subtitle="These details appear on printed bill headers and customer communications."
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Field
                    label="Company / Store Name *"
                    id="company-name"
                    error={errors.company_name}
                  >
                    <div className="relative">
                      <Building2 className="absolute left-3 top-2.5 h-3.5 w-3.5 text-slate-400" />
                      <input
                        id="company-name"
                        type="text"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        placeholder="e.g. Threads Textile Store"
                        className={`${inputCls(!!errors.company_name)} pl-9`}
                      />
                    </div>
                  </Field>
                </div>

                <div className="md:col-span-2">
                  <Field label="Store Address" id="company-address">
                    <div className="relative">
                      <MapPin className="absolute left-3 top-2.5 h-3.5 w-3.5 text-slate-400" />
                      <textarea
                        id="company-address"
                        value={companyAddress}
                        onChange={(e) => setCompanyAddress(e.target.value)}
                        placeholder="e.g. 12/A Ring Road, Textile Market, Surat - 395002"
                        rows={2}
                        className={`${inputCls()} pl-9 resize-none`}
                      />
                    </div>
                  </Field>
                </div>

                <Field label="Contact Mobile" id="company-mobile" error={errors.company_mobile}>
                  <div className="relative">
                    <Phone className="absolute left-3 top-2.5 h-3.5 w-3.5 text-slate-400" />
                    <input
                      id="company-mobile"
                      type="tel"
                      value={companyMobile}
                      onChange={(e) => setCompanyMobile(e.target.value)}
                      placeholder="e.g. 9998877766"
                      maxLength={10}
                      className={`${inputCls(!!errors.company_mobile)} pl-9`}
                    />
                  </div>
                </Field>
              </div>
            </div>

            {/* ── Section B: GST Governance Toggle ─────────────────── */}
            {tenantFlags.gstComplianceExport && (
              <div className="card border border-slate-200">
                <SectionHeader
                  icon={ShieldCheck}
                  title="GST Governance Engine"
                  subtitle="Enable or disable tax computation across all POS transactions for this store."
                />

              {/* Toggle Row */}
              <div
                className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all duration-200 ${isGstEligible
                    ? 'bg-emerald-50 border-emerald-300'
                    : 'bg-slate-50 border-slate-200'
                  }`}
              >
                <div className="space-y-0.5">
                  <p className="text-sm font-bold text-slate-800">
                    Activate GST Billing &amp; Tax Engine
                  </p>
                  <p className="text-[11px] text-slate-500 font-light">
                    {isGstEligible
                      ? 'GST is ACTIVE — taxes will be computed on all invoices.'
                      : 'GST is OFF — all invoices will be tax-free (₹0 tax).'}
                  </p>
                </div>
                <button
                  type="button"
                  id="gst-toggle"
                  onClick={() => setIsGstEligible((p) => !p)}
                  className="flex-shrink-0 cursor-pointer focus:outline-none"
                  aria-pressed={isGstEligible}
                >
                  {isGstEligible ? (
                    <ToggleRight size={44} className="text-emerald-500 drop-shadow-sm" />
                  ) : (
                    <ToggleLeft size={44} className="text-slate-300" />
                  )}
                </button>
              </div>

              {/* GST ON — reveal GSTIN + Slab */}
              {isGstEligible && (
                <div className="mt-5 space-y-5 animate-fadeIn">
                  {/* GSTIN Input */}
                  <Field
                    label="Tenant GSTIN Registration Number"
                    id="tenant-gstin"
                    error={errors.tenant_gstin}
                  >
                    <div className="relative">
                      <ShieldCheck className="absolute left-3 top-2.5 h-3.5 w-3.5 text-slate-400" />
                      <input
                        id="tenant-gstin"
                        type="text"
                        value={tenantGstin}
                        onChange={(e) => setTenantGstin(e.target.value.toUpperCase())}
                        placeholder="e.g. 29ABCDE1234F1Z5"
                        maxLength={15}
                        className={`${inputCls(!!errors.tenant_gstin)} pl-9 pr-9 font-mono uppercase tracking-widest`}
                      />
                      {gstinTouched && (
                        <div className="absolute right-3 top-2.5">
                          {gstinValid ? (
                            <CheckCircle2 size={14} className="text-emerald-500" />
                          ) : (
                            <AlertCircle size={14} className="text-slate-300" />
                          )}
                        </div>
                      )}
                    </div>
                    {gstinTouched && !gstinValid && (
                      <p className="text-[10px] text-slate-400 font-light mt-1">
                        Format: 2-digit state code + 5 letters + 4 digits + 1 letter + 1 char + Z +
                        1 char
                      </p>
                    )}
                  </Field>

                  {/* ── Section C: Dynamic Textile Tax Slab ─────────── */}
                  <div className="border border-emerald-100 rounded-xl overflow-hidden">
                    {/* Slab Header (collapsible) */}
                    <button
                      type="button"
                      onClick={() => setSlabExpanded((p) => !p)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-emerald-50 hover:bg-emerald-100 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-2.5">
                        <Sliders size={15} className="text-emerald-600" />
                        <span className="text-xs font-bold text-emerald-800 uppercase tracking-wide">
                          Dynamic Textile Tax Slab Configurator
                        </span>
                      </div>
                      {slabExpanded ? (
                        <ChevronUp size={15} className="text-emerald-600" />
                      ) : (
                        <ChevronDown size={15} className="text-emerald-600" />
                      )}
                    </button>

                    {slabExpanded && (
                      <div className="p-4 space-y-4 bg-white">
                        <p className="text-[11px] text-slate-500 font-light leading-relaxed">
                          Define a two-tier pricing boundary. Items priced at or below the threshold
                          will use the <strong>lower GST rate</strong>; items above will use the{' '}
                          <strong>higher GST rate</strong>.
                        </p>

                        {/* Slab Preview Badge */}
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-800 text-white rounded-full text-[10px] font-mono font-bold shadow">
                          <BadgePercent size={11} />
                          <span>
                            ₹0 – ₹{slab.threshold_amount.toLocaleString('en-IN')} @{' '}
                            {slab.low_rate_percentage}% &nbsp;|&nbsp; Above ₹
                            {slab.threshold_amount.toLocaleString('en-IN')} @{' '}
                            {slab.high_rate_percentage}%
                          </span>
                        </div>

                        {/* Three input fields */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <Field
                            label="Price Threshold Limit (₹)"
                            id="slab-threshold"
                            error={errors.threshold_amount}
                          >
                            <div className="relative">
                              <IndianRupee className="absolute left-3 top-2.5 h-3.5 w-3.5 text-slate-400" />
                              <input
                                id="slab-threshold"
                                type="number"
                                min={1}
                                value={slab.threshold_amount}
                                onChange={(e) =>
                                  setSlab((p) => ({
                                    ...p,
                                    threshold_amount: parseFloat(e.target.value) || 0,
                                  }))
                                }
                                className={`${inputCls(!!errors.threshold_amount)} pl-9 font-mono`}
                              />
                            </div>
                          </Field>

                          <Field
                            label="GST Rate ≤ Threshold (%)"
                            id="slab-low-rate"
                            error={errors.low_rate}
                          >
                            <div className="relative">
                              <BadgePercent className="absolute left-3 top-2.5 h-3.5 w-3.5 text-emerald-500" />
                              <input
                                id="slab-low-rate"
                                type="number"
                                min={0}
                                max={28}
                                step={0.5}
                                value={slab.low_rate_percentage}
                                onChange={(e) =>
                                  setSlab((p) => ({
                                    ...p,
                                    low_rate_percentage: parseFloat(e.target.value) || 0,
                                  }))
                                }
                                className={`${inputCls(!!errors.low_rate)} pl-9 font-mono`}
                              />
                            </div>
                          </Field>

                          <Field
                            label="GST Rate > Threshold (%)"
                            id="slab-high-rate"
                            error={errors.high_rate}
                          >
                            <div className="relative">
                              <BadgePercent className="absolute left-3 top-2.5 h-3.5 w-3.5 text-amber-500" />
                              <input
                                id="slab-high-rate"
                                type="number"
                                min={0}
                                max={28}
                                step={0.5}
                                value={slab.high_rate_percentage}
                                onChange={(e) =>
                                  setSlab((p) => ({
                                    ...p,
                                    high_rate_percentage: parseFloat(e.target.value) || 0,
                                  }))
                                }
                                className={`${inputCls(!!errors.high_rate)} pl-9 font-mono`}
                              />
                            </div>
                          </Field>
                        </div>

                        {/* Visual matrix reference */}
                        <div className="grid grid-cols-2 gap-2 mt-1">
                          <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-2.5 text-center">
                            <p className="text-[9px] uppercase font-bold text-emerald-500 tracking-widest mb-0.5">
                              LOWER BAND
                            </p>
                            <p className="text-xs font-mono font-bold text-emerald-800">
                              ≤ ₹{slab.threshold_amount.toLocaleString('en-IN')}
                            </p>
                            <p className="text-lg font-extrabold text-emerald-700 mt-0.5">
                              {slab.low_rate_percentage}%
                            </p>
                            <p className="text-[9px] text-emerald-500 font-light">
                              CGST {(slab.low_rate_percentage / 2).toFixed(1)}% + SGST{' '}
                              {(slab.low_rate_percentage / 2).toFixed(1)}%
                            </p>
                          </div>
                          <div className="bg-amber-50 border border-amber-100 rounded-lg p-2.5 text-center">
                            <p className="text-[9px] uppercase font-bold text-amber-500 tracking-widest mb-0.5">
                              UPPER BAND
                            </p>
                            <p className="text-xs font-mono font-bold text-amber-800">
                              &gt; ₹{slab.threshold_amount.toLocaleString('en-IN')}
                            </p>
                            <p className="text-lg font-extrabold text-amber-700 mt-0.5">
                              {slab.high_rate_percentage}%
                            </p>
                            <p className="text-[9px] text-amber-500 font-light">
                              CGST {(slab.high_rate_percentage / 2).toFixed(1)}% + SGST{' '}
                              {(slab.high_rate_percentage / 2).toFixed(1)}%
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            )}

            {/* ── Section D: Loyalty Program Configurator ──────────── */}
            {tenantFlags.loyaltyEngine && (
              <div className="card">
                <SectionHeader
                  icon={Sliders}
                  title="Loyalty Reward Program"
                  subtitle="Configure automatic loyalty points accrual rules based on client cart spend."
                />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Field
                    label="For Every Spend of (₹) *"
                    id="loyalty-spend-threshold"
                    error={errors.loyalty_spend_threshold}
                  >
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-xs font-bold text-slate-400">
                        ₹
                      </span>
                      <input
                        id="loyalty-spend-threshold"
                        type="number"
                        min={1}
                        value={loyaltySpendThreshold}
                        onChange={(e) => setLoyaltySpendThreshold(parseFloat(e.target.value) || 0)}
                        className={`${inputCls(!!errors.loyalty_spend_threshold)} pl-8 font-mono`}
                      />
                    </div>
                  </Field>
                </div>

                <div>
                  <Field
                    label="Reward Points Granted *"
                    id="loyalty-points-rewarded"
                    error={errors.loyalty_points_rewarded}
                  >
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-xs font-bold text-slate-400">
                        Pts
                      </span>
                      <input
                        id="loyalty-points-rewarded"
                        type="number"
                        min={1}
                        value={loyaltyPointsRewarded}
                        onChange={(e) => setLoyaltyPointsRewarded(parseInt(e.target.value) || 0)}
                        className={`${inputCls(!!errors.loyalty_points_rewarded)} pl-10 font-mono`}
                      />
                    </div>
                  </Field>
                </div>
              </div>

              <div className="mt-4 p-3 bg-emerald-50/50 rounded-lg border border-emerald-100/50 text-[11px] text-slate-600 leading-relaxed">
                💡 <span className="font-bold text-emerald-800">Accrual Calculation Rule:</span>{' '}
                Purchasing client will earn{' '}
                <span className="font-bold text-emerald-700 font-mono">
                  {loyaltyPointsRewarded} loyalty points
                </span>{' '}
                for every{' '}
                <span className="font-bold text-emerald-700 font-mono">
                  ₹{loyaltySpendThreshold}
                </span>{' '}
                spent. (e.g. For a bill of{' '}
                <span className="font-bold font-mono">
                  ₹{(loyaltySpendThreshold * 3.5).toFixed(0)}
                </span>
                , customer will receive{' '}
                <span className="font-bold text-emerald-700 font-mono">
                  {loyaltyPointsRewarded * 3} points
                </span>
                ).
              </div>
            </div>
            )}

            {/* ── Section D2: Invoice Number Customization ────────── */}
            <div className="card">
              <SectionHeader
                icon={Sliders}
                title="Invoice Sequence Configuration"
                subtitle="Customize the prefix and starting sequence number for generated sales invoices."
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Field
                    label="Invoice Prefix *"
                    id="invoice-prefix"
                    error={errors.invoice_prefix}
                  >
                    <input
                      id="invoice-prefix"
                      type="text"
                      value={invoicePrefix}
                      onChange={(e) => setInvoicePrefix(e.target.value.toUpperCase())}
                      placeholder="e.g. INV-"
                      className={inputCls(!!errors.invoice_prefix)}
                    />
                  </Field>
                </div>

                <div>
                  <Field
                    label="Starting Sequence Number *"
                    id="next-invoice-number"
                    error={errors.next_invoice_number}
                  >
                    <input
                      id="next-invoice-number"
                      type="number"
                      min={1}
                      value={nextInvoiceNumber}
                      onChange={(e) => setNextInvoiceNumber(parseInt(e.target.value) || 0)}
                      className={`${inputCls(!!errors.next_invoice_number)} font-mono`}
                    />
                  </Field>
                </div>
              </div>
            </div>

            {/* ── Section E: Print & Sticker Configurator ─────────── */}
            <div className="card">
              <SectionHeader
                icon={Printer}
                title="Print Copy & Sticker Customization"
                subtitle="Configure layout preferences for physical thermal receipts and barcode label stickers."
              />

              <div className="space-y-6">
                {/* Receipt Options */}
                <div>
                  <h4 className="text-xs font-bold text-slate-700 mb-3 border-b pb-1.5 flex items-center gap-1.5">
                    <Printer size={13} className="text-emerald-600" />
                    POS Invoice Receipt Customization
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Receipt Roll Width" id="paper-size">
                      <select
                        id="paper-size"
                        value={printConfig.paper_size}
                        onChange={(e) =>
                          setPrintConfig((prev) => ({
                            ...prev,
                            paper_size: e.target.value as '58mm' | '80mm',
                          }))
                        }
                        className={inputCls()}
                      >
                        <option value="58mm">58mm (2-inch Printer - Compact)</option>
                        <option value="80mm">80mm (3-inch Printer - Standard)</option>
                      </select>
                    </Field>

                    <Field label="Printer Connection Mode" id="connection-mode">
                      <select
                        id="connection-mode"
                        value={printConfig.connection_mode || 'BLUETOOTH'}
                        onChange={(e) =>
                          setPrintConfig((prev) => ({
                            ...prev,
                            connection_mode: e.target.value as 'BLUETOOTH' | 'USB',
                          }))
                        }
                        className={inputCls()}
                      >
                        <option value="BLUETOOTH">Bluetooth Mode (Wireless BLE)</option>
                        <option value="USB">USB Mode (Wired / Browser print)</option>
                      </select>
                    </Field>

                    <Field
                      label="Receipt Footer Custom Message"
                      id="footer-message"
                      error={errors.footer_message}
                    >
                      <input
                        id="footer-message"
                        type="text"
                        maxLength={120}
                        value={printConfig.footer_message}
                        onChange={(e) =>
                          setPrintConfig((prev) => ({
                            ...prev,
                            footer_message: e.target.value,
                          }))
                        }
                        placeholder="e.g. Thank you for your visit!"
                        className={inputCls(!!errors.footer_message)}
                      />
                    </Field>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mt-4">
                    {[
                      { key: 'show_address', label: 'Show Store Address' },
                      { key: 'show_mobile', label: 'Show Contact Mobile' },
                      { key: 'show_gstin', label: 'Show Store GSTIN' },
                      { key: 'show_customer', label: 'Show Customer Info' },
                      { key: 'show_loyalty', label: 'Show Loyalty Points' },
                    ].map((item) => (
                      <label
                        key={item.key}
                        className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors text-xs font-medium text-slate-700"
                      >
                        <input
                          type="checkbox"
                          checked={!!printConfig[item.key as keyof PrintConfig]}
                          onChange={(e) =>
                            setPrintConfig((prev) => ({
                              ...prev,
                              [item.key]: e.target.checked,
                            }))
                          }
                          className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 h-3.5 w-3.5 cursor-pointer"
                        />
                        {item.label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Sticker Label Options */}
                <div>
                  <h4 className="text-xs font-bold text-slate-700 mb-3 border-b pb-1.5 flex items-center gap-1.5">
                    <Tag size={13} className="text-emerald-600" />
                    Barcode / Sticker Label Customization
                  </h4>
                  <div className="grid grid-cols-1 gap-4">
                    <Field label="Sticker Label Physical Size" id="label-size">
                      <select
                        id="label-size"
                        value={printConfig.label_size}
                        onChange={(e) =>
                          setPrintConfig((prev) => ({
                            ...prev,
                            label_size: e.target.value as '2x1' | '3x2' | '4x3',
                          }))
                        }
                        className={inputCls()}
                      >
                        <option value="2x1">2" x 1" Sticker (Jewellery/Small items)</option>
                        <option value="3x2">3" x 2" Sticker (Standard Textile Tag)</option>
                        <option value="4x3">4" x 3" Sticker (Large Shipping/Box Label)</option>
                      </select>
                    </Field>
                  </div>

                  <div className="grid grid-cols-3 gap-3 mt-4">
                    {[
                      { key: 'show_brand', label: 'Brand Title' },
                      { key: 'show_category', label: 'Category' },
                      { key: 'show_mrp', label: 'MRP Price' },
                    ].map((item) => (
                      <label
                        key={item.key}
                        className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors text-xs font-medium text-slate-700"
                      >
                        <input
                          type="checkbox"
                          checked={!!printConfig[item.key as keyof PrintConfig]}
                          onChange={(e) =>
                            setPrintConfig((prev) => ({
                              ...prev,
                              [item.key]: e.target.checked,
                            }))
                          }
                          className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 h-3.5 w-3.5 cursor-pointer"
                        />
                        {item.label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Save Button ──────────────────────────────────────── */}
            <div className="flex items-center justify-end gap-3 pt-2 pb-6">
              {saveStatus === 'success' && (
                <div className="flex items-center gap-1.5 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-xs font-semibold animate-fadeIn">
                  <CheckCircle2 size={14} />
                  Configuration saved successfully!
                </div>
              )}
              {saveStatus === 'error' && (
                <div className="flex items-center gap-1.5 text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-xs font-semibold animate-fadeIn">
                  <AlertCircle size={14} />
                  Failed to save settings.
                </div>
              )}
              <button
                type="submit"
                id="save-company-settings"
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg shadow-md hover:shadow-lg active:scale-[0.98] transition-all cursor-pointer border border-emerald-500"
              >
                {saving ? (
                  <>
                    <Loader2 size={15} className="animate-spin" />
                    Saving Configuration…
                  </>
                ) : saveStatus === 'success' ? (
                  <>
                    <CheckCircle2 size={15} />
                    Saved!
                  </>
                ) : (
                  <>
                    <Save size={15} />
                    Save Company Settings
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* ── Live Sticky Print Previews (Right Hand Column) ────────────────── */}
        <div className="lg:col-span-5 space-y-6">
          <div className="sticky top-6 space-y-6">
            {/* Invoice Thermal Preview */}
            <div className="bg-slate-900 text-slate-100 p-5 rounded-2xl border border-slate-800 shadow-xl">
              <div className="flex items-center justify-between border-b border-slate-800 pb-3 mb-4">
                <span className="text-[11px] uppercase font-bold text-slate-400 tracking-wider flex items-center gap-1.5">
                  <Printer size={12} className="text-emerald-500 animate-pulse" />
                  Thermal Receipt Preview ({printConfig.paper_size})
                </span>
                <span className="text-[9px] bg-slate-800 border border-slate-700 px-2 py-0.5 rounded text-slate-400 font-mono">
                  Live Mock
                </span>
              </div>

              {/* Simulated Thermal Paper Roll */}
              <div className="flex justify-center bg-slate-950 p-4 rounded-xl border border-slate-850 overflow-hidden">
                <div
                  className={`bg-white text-slate-900 p-4 shadow-inner border-x border-slate-200 transition-all duration-300 font-mono text-[10px] leading-relaxed`}
                  style={{
                    width: printConfig.paper_size === '58mm' ? '240px' : '320px',
                    fontSize: printConfig.paper_size === '58mm' ? '9px' : '11px',
                  }}
                >
                  {/* Header */}
                  <div className="text-center font-bold uppercase tracking-wider text-xs border-b border-dashed border-slate-400 pb-2 mb-2">
                    {companyName || 'STORE NAME'}
                  </div>

                  {printConfig.show_address && companyAddress && (
                    <div className="text-center text-slate-500 mb-1 leading-snug">
                      {companyAddress}
                    </div>
                  )}

                  {printConfig.show_mobile && companyMobile && (
                    <div className="text-center text-slate-500 mb-1">Mob: {companyMobile}</div>
                  )}

                  {printConfig.show_gstin && isGstEligible && tenantGstin && (
                    <div className="text-center font-bold text-slate-700 mb-2">
                      GSTIN: {tenantGstin}
                    </div>
                  )}

                  {/* Tx info */}
                  <div className="border-b border-dashed border-slate-350 py-1.5 my-1.5 flex justify-between text-[9px] text-slate-500 font-semibold uppercase">
                    <span>Inv: #TX-9042</span>
                    <span>02/06/2026</span>
                  </div>

                  {printConfig.show_customer && (
                    <div className="border-b border-dashed border-slate-350 pb-1.5 mb-1.5 text-slate-650 leading-tight">
                      <span className="font-bold">Customer:</span> R. Sharma
                      <br />
                      <span className="font-bold">Mobile:</span> 9876543210
                    </div>
                  )}

                  {/* Items */}
                  <div className="space-y-1 my-2">
                    <div className="flex justify-between font-bold border-b border-slate-200 pb-0.5">
                      <span>ITEM [QTY]</span>
                      <span>AMT</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Cotton Kurti (XL) [x2]</span>
                      <span>₹1,598.00</span>
                    </div>
                    <div className="flex justify-between text-slate-500">
                      <span className="pl-2">└ GST @ 12%</span>
                      <span>Included</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Designer Saree [x1]</span>
                      <span>₹2,499.00</span>
                    </div>
                    <div className="flex justify-between text-slate-500">
                      <span className="pl-2">└ GST @ 12%</span>
                      <span>Included</span>
                    </div>
                  </div>

                  {/* Totals */}
                  <div className="border-t border-dashed border-slate-400 pt-2 mt-2 space-y-1 font-bold">
                    <div className="flex justify-between">
                      <span>SUBTOTAL</span>
                      <span>₹4,097.00</span>
                    </div>
                    {isGstEligible && (
                      <div className="flex justify-between text-slate-500 font-normal">
                        <span>CGST/SGST Included</span>
                        <span>₹438.96</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xs border-t border-slate-900 pt-1 mt-1 font-extrabold uppercase">
                      <span>GRAND TOTAL</span>
                      <span>₹4,097.00</span>
                    </div>
                  </div>

                  {printConfig.show_loyalty && (
                    <div className="border-t border-dashed border-slate-350 pt-2 mt-2 bg-slate-50 p-1.5 rounded text-center text-emerald-800 font-semibold">
                      ★ Loyalty Points Earned: +
                      {((4097 / loyaltySpendThreshold) * loyaltyPointsRewarded).toFixed(0)} Pts ★
                    </div>
                  )}

                  {/* Footer */}
                  <div className="text-center text-slate-400 mt-4 border-t border-dashed border-slate-350 pt-3 italic leading-relaxed">
                    {printConfig.footer_message || 'Thank you for your visit!'}
                  </div>

                  {/* Decorative paper cut edge */}
                  <div className="mt-4 flex justify-between text-slate-200 select-none opacity-40">
                    <span>/\/\/\/\/\/\/\/\/\/\/\</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Sticker Label Preview */}
            <div className="bg-slate-900 text-slate-100 p-5 rounded-2xl border border-slate-800 shadow-xl">
              <div className="flex items-center justify-between border-b border-slate-800 pb-3 mb-4">
                <span className="text-[11px] uppercase font-bold text-slate-400 tracking-wider flex items-center gap-1.5">
                  <Tag size={12} className="text-emerald-500" />
                  Sticker Label Preview ({printConfig.label_size})
                </span>
                <span className="text-[9px] bg-slate-800 border border-slate-700 px-2 py-0.5 rounded text-slate-400 font-mono">
                  Live Mock
                </span>
              </div>

              <div className="flex justify-center bg-slate-950 p-6 rounded-xl border border-slate-850 overflow-hidden min-h-[220px] items-center">
                {/* Simulated Label Sticker */}
                <div
                  className="bg-amber-50/90 text-slate-900 rounded-lg p-3 border border-amber-200/50 shadow-inner flex flex-col justify-between transition-all duration-300 font-mono text-[9px] relative overflow-hidden"
                  style={{
                    width:
                      printConfig.label_size === '2x1'
                        ? '220px'
                        : printConfig.label_size === '3x2'
                          ? '280px'
                          : '340px',
                    height:
                      printConfig.label_size === '2x1'
                        ? '110px'
                        : printConfig.label_size === '3x2'
                          ? '170px'
                          : '220px',
                  }}
                >
                  {/* Top Section */}
                  <div className="text-center">
                    {/* Brand */}
                    {printConfig.show_brand && (
                      <div className="font-extrabold uppercase text-[10px] tracking-wider text-slate-800 border-b border-dashed border-slate-300 pb-1 text-center">
                        {companyName || 'BRAND STORE'}
                      </div>
                    )}

                    {/* SKU, Size & Price Details */}
                    <div className="mt-1 leading-tight text-center">
                      <span className="font-bold text-slate-900 text-[10px] block">Silk Saree Banarasi</span>
                      <div className="text-[7.5px] text-slate-500 font-medium flex justify-center gap-1.5 mt-0.5">
                        <span>SKU: BOY-BL-M</span>
                        <span>|</span>
                        <span className="font-bold text-slate-700">PRICE: ₹3,499.00</span>
                      </div>
                    </div>
                  </div>

                  {/* Barcode/QR Code Container (Center) */}
                  <div className="flex flex-col items-center justify-center my-1.5 flex-1 w-full">
                    {/* SVG Barcode mock - taller/bigger bars */}
                    <svg className="w-11/12 h-[42px] transform scale-[1.12]" viewBox="0 0 100 24" preserveAspectRatio="none">
                      <rect x="0" y="0" width="3" height="24" fill="black" />
                      <rect x="5" y="0" width="1.5" height="24" fill="black" />
                      <rect x="9" y="0" width="4.5" height="24" fill="black" />
                      <rect x="16" y="0" width="1.5" height="24" fill="black" />
                      <rect x="20" y="0" width="3" height="24" fill="black" />
                      <rect x="26" y="0" width="6" height="24" fill="black" />
                      <rect x="34" y="0" width="1.5" height="24" fill="black" />
                      <rect x="38" y="0" width="3" height="24" fill="black" />
                      <rect x="44" y="0" width="4.5" height="24" fill="black" />
                      <rect x="51" y="0" width="1.5" height="24" fill="black" />
                      <rect x="55" y="0" width="6" height="24" fill="black" />
                      <rect x="63" y="0" width="3" height="24" fill="black" />
                      <rect x="68" y="0" width="1.5" height="24" fill="black" />
                      <rect x="72" y="0" width="4.5" height="24" fill="black" />
                      <rect x="79" y="0" width="3" height="24" fill="black" />
                      <rect x="84" y="0" width="1.5" height="24" fill="black" />
                      <rect x="88" y="0" width="6" height="24" fill="black" />
                      <rect x="96" y="0" width="3" height="24" fill="black" />
                    </svg>
                    <span className="text-[6.5px] text-slate-650 font-mono tracking-widest mt-1.5 font-bold">
                      *890123456789*
                    </span>
                  </div>

                  {/* Price Details (Bottom) */}
                  <div className="border-t border-dashed border-slate-300 pt-1.5 flex items-center justify-center gap-2 font-semibold">
                    {printConfig.show_mrp && (
                      <>
                        <span className="text-[7.5px] text-slate-400 line-through">MRP: ₹4,999.00</span>
                        <span className="text-[6px] text-emerald-600 font-bold uppercase">(Save 30%)</span>
                      </>
                    )}
                    <div className="font-extrabold text-[11px] text-slate-900">SALE: ₹3,499.00</div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
